import { FC, useRef } from 'react';

import { useTranslation } from 'libs/i18n';
import { Chip } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import {
  DialogDeleteAndReasonChange,
  DialogReasonChange,
  ClientFormContent,
} from 'modules/client/common/components';
import {
  ActionProps,
  ActionType,
} from 'modules/client/common/components/ClientFormContent/types';
import {
  LockAndUnlockReadOnlyProps,
  useLockAndUnlockReadOnly,
} from 'modules/client/common/hooks';

import { useFormContext } from '../../../forms/legalClientLegalHeadForm';
import {
  LegalClientLegalHeadSections,
  LegalClientLegalHeadSectionsProps,
} from '../../LegalClientLegalHeadSections';

export interface FormContentProps
  extends LockAndUnlockReadOnlyProps,
    LegalClientLegalHeadSectionsProps {
  submitLoading?: boolean;
  deleteLoading?: boolean;
  archiveLoading?: boolean;
  dearchiveLoading?: boolean;
  onShowVersionHistory?: () => void;
  onDelete?: (reason: string) => void;
  onArchive?: (reason: string) => Promise<boolean>;
  onDearchive?: (reason: string) => Promise<boolean>;
}

export const FormContent: FC<FormContentProps> = ({
  submitLoading,
  deleteLoading,
  archiveLoading,
  dearchiveLoading,
  lockClientIfPossible,
  unlockClient,
  onShowVersionHistory,
  onDelete,
  onArchive,
  onDearchive,
  ...sectionsProps
}) => {
  const { t } = useTranslation();
  const { resetForm, initialValues, setReadOnly } = useFormContext();

  const companyFullName = initialValues.companyName.fullName;

  const headerTitle = t('client.legal.legalClientLegalHead.title', {
    name: companyFullName,
  });

  const { onDisableReadOnly, onEnableReadOnly } = useLockAndUnlockReadOnly(
    setReadOnly,
    { unlockClient, lockClientIfPossible }
  );

  const { open: openDeleteDialog, ...deleteDialogProps } = useDialog();
  const { open: openDialogReason, ...dialogReasonProps } = useDialog();

  const actionRef = useRef<ActionType>();

  const isArchived = onDearchive !== undefined;

  const onActionArchive = () => {
    actionRef.current = ActionType.archive;
    openDialogReason();
  };

  const onActionDearchive = () => {
    actionRef.current = ActionType.dearchive;
    openDialogReason();
  };

  const actionMenuProps: ActionProps = {
    showEditAction: !isArchived,
    onDelete: onDelete ? openDeleteDialog : undefined,
    onArchive: onArchive ? onActionArchive : undefined,
    onDearchive: onDearchive ? onActionDearchive : undefined,
  };

  const onSaveReason = async (reason: string) => {
    if (actionRef.current === ActionType.archive) {
      if (await onArchive?.(reason)) {
        dialogReasonProps.onClose();
      }
    } else if (actionRef.current === ActionType.dearchive) {
      if (await onDearchive?.(reason)) {
        dialogReasonProps.onClose();
      }
    }
  };

  return (
    <>
      <ClientFormContent
        actionMenuProps={actionMenuProps}
        content={<LegalClientLegalHeadSections {...sectionsProps} />}
        headerAfterContent={
          <Chip
            size="s"
            text={
              isArchived ? t('common.archivedStatus') : t('common.activeStatus')
            }
            variant={isArchived ? 'gray' : 'green'}
          />
        }
        headerTitle={headerTitle}
        resetForm={resetForm}
        shownTabBar={false}
        submitLoading={submitLoading}
        onEdit={onDisableReadOnly}
        onEnableReadOnly={onEnableReadOnly}
        onShowVersionHistory={onShowVersionHistory}
      />
      {onDelete && (
        <DialogDeleteAndReasonChange
          {...deleteDialogProps}
          deleteLoading={deleteLoading}
          onDelete={onDelete}
        />
      )}

      <DialogReasonChange
        submitLoading={archiveLoading ?? dearchiveLoading}
        onSave={onSaveReason}
        {...dialogReasonProps}
      />
    </>
  );
};
